import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  THEMES,
  ThemeId,
  THEME_IDS,
  THEME_UNLOCKS,
  isThemeUnlocked,
} from "@/lib/themes";
import { useTheme } from "@/context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// ThemeMenu — a horizontal grid of theme cards. Each card shows three
// preview swatches and the theme's name + tagline. Tapping a card calls
// setTheme(), which writes to localStorage + profiles.theme and applies the
// CSS vars instantly. Used in the splash screen and in SettingsSheet.
// ─────────────────────────────────────────────────────────────────────────────

export function ThemeMenu({
  compact = false,
  onPick,
}: {
  /** When true, the cards stack 2-per-row instead of 1-per-row. */
  compact?: boolean;
  /** Optional callback after a theme is chosen (e.g. close a sheet). */
  onPick?: (id: ThemeId) => void;
}) {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  // Posted-scans count drives the unlock gate. Anonymous users see all
  // gated themes as locked except free ones; signing in reveals progress.
  const [postedCount, setPostedCount] = useState<number>(0);

  useEffect(() => {
    if (!user) { setPostedCount(0); return; }
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from("scans")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("posted", true);
      if (!cancelled && typeof count === "number") setPostedCount(count);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr 1fr" : "1fr",
        gap: 10,
      }}
    >
      {THEME_IDS.map((id) => {
        const meta = THEMES[id];
        const active = theme === id;
        const unlock = THEME_UNLOCKS[id];
        const unlocked = isThemeUnlocked(id, postedCount);
        const remaining = Math.max(0, unlock.postedScans - postedCount);
        return (
          <button
            key={id}
            type="button"
            onClick={async () => {
              if (!unlocked) {
                toast.message("Theme locked", {
                  description: unlock.hint || `Post ${remaining} more to unlock.`,
                });
                return;
              }
              await setTheme(id);
              onPick?.(id);
            }}
            aria-pressed={active}
            aria-disabled={!unlocked}
            style={{
              textAlign: "left",
              background: "hsl(var(--cream))",
              border: active
                ? "2px solid hsl(var(--rust))"
                : "1px solid hsl(var(--ink))",
              padding: compact ? "10px 10px" : "12px 14px",
              cursor: unlocked ? "pointer" : "not-allowed",
              opacity: unlocked ? 1 : 0.55,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              position: "relative",
            }}
          >
            {!unlocked && (
              <span
                className="caps my-px"
                style={{
                  position: "absolute",
                  top: 6, right: 8,
                  color: "hsl(var(--ink-2))",
                  fontSize: 9,
                }}
              >🔒 {remaining > 0 ? `${remaining} more` : "Locked"}</span>
            )}
            <div style={{ display: "flex", gap: 4 }}>
              {meta.preview.map((hsl, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: compact ? 22 : 28,
                    background: `hsl(${hsl})`,
                    border: "1px solid hsl(var(--ink))",
                  }}
                />
              ))}
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 700,
                  fontSize: compact ? 14 : 16,
                  color: "hsl(var(--ink))",
                  lineHeight: 1.1,
                }}
              >
                {meta.name}
              </div>
              <div
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.06em",
                  color: "hsl(var(--ink-2))",
                  marginTop: 4,
                  lineHeight: 1.3,
                }}
              >
                {unlocked ? meta.tagline : unlock.hint}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}